/**
 * BrandLogo — single component used by every in-app header that shows
 * the brand mark. When the super admin uploads a custom PWA icon, this
 * component renders the uploaded image; otherwise it falls back to the
 * stock Phosphor ``Database`` icon for visual continuity with prior
 * versions.
 *
 * Why a dedicated component rather than inline ``<img>``?
 *   * Centralises the "do we have a custom icon?" decision so every
 *     header treats it the same way (branding has overrides → image,
 *     else → fallback icon).
 *   * Keeps the icon ``size`` prop consistent (Phosphor uses a number,
 *     <img> uses CSS) so callers don't have to special-case.
 *   * One place to swap the rendered surface (e.g. switch from a flat
 *     <img> to a rounded badge) when the brand evolves.
 *
 * Usage
 * -----
 *   <BrandLogo size={22} className="text-blue-700 shrink-0" />
 */
import { Database } from "@phosphor-icons/react";
import { useBranding } from "@/lib/branding";

export default function BrandLogo({
  size = 22,
  className = "text-blue-700 shrink-0",
  variant = "any-192",
}) {
  const branding = useBranding();
  const hasCustom = Boolean(branding.has_overrides && branding.icons?.[variant]);

  if (hasCustom) {
    return (
      <img
        src={branding.icons[variant]}
        alt=""
        width={size}
        height={size}
        style={{ width: size, height: size, objectFit: "contain" }}
        className={`${className} rounded-sm`}
        data-testid="brand-logo-img"
      />
    );
  }

  return (
    <Database
      size={size}
      weight="bold"
      className={className}
      data-testid="brand-logo-fallback"
    />
  );
}
